import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Button, Container, Typography } from '@mui/material';

const ADMIN_VISUAL = `${process.env.PUBLIC_URL}/assets/AdminView.jpg`;
const THERAPIST_VISUAL = `${process.env.PUBLIC_URL}/assets/TheraoistView.jpg`;

const PlatformVisual = ({ src, alt, badge, placeholderLabel }) => {
    const [showImg, setShowImg] = React.useState(true);

    return (
        <Box
            sx={{
                position: 'relative',
                borderRadius: '20px',
                overflow: 'hidden',
                border: '1px solid rgba(0, 102, 255, 0.14)',
                background:
                    'linear-gradient(165deg, #FFFFFF 0%, #F4F7FF 38%, #EEF2FF 100%)',
                boxShadow:
                    '0 20px 50px rgba(15, 23, 42, 0.08), 0 0 0 1px rgba(255, 255, 255, 0.6) inset',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: -1,
                    borderRadius: '21px',
                    padding: '1px',
                    background: 'linear-gradient(145deg, rgba(0, 102, 255, 0.35), rgba(99, 102, 241, 0.12), rgba(0, 212, 255, 0.2))',
                    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                    WebkitMaskComposite: 'xor',
                    maskComposite: 'exclude',
                    pointerEvents: 'none',
                    opacity: 0.85,
                },
            }}
        >
            {badge && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 14,
                        left: 14,
                        zIndex: 2,
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 999,
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        letterSpacing: '0.12em',
                        textTransform: 'uppercase',
                        color: '#1E40AF',
                        bgcolor: 'rgba(255, 255, 255, 0.92)',
                        border: '1px solid rgba(0, 102, 255, 0.18)',
                        boxShadow: '0 4px 14px rgba(15, 23, 42, 0.06)',
                    }}
                >
                    {badge}
                </Box>
            )}
            <Box
                sx={{
                    p: { xs: 2, md: 2.5 },
                    pt: badge ? { xs: 5.5, md: 6 } : { xs: 2, md: 2.5 },
                    minHeight: { xs: 240, sm: 280, md: 320 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                {showImg ? (
                    <Box
                        component="img"
                        src={src}
                        alt={alt}
                        onError={() => setShowImg(false)}
                        sx={{
                            width: '100%',
                            height: 'auto',
                            maxHeight: { xs: 280, md: 360 },
                            objectFit: 'contain',
                            display: 'block',
                            filter: 'drop-shadow(0 12px 28px rgba(15, 23, 42, 0.08))',
                        }}
                    />
                ) : (
                    <Typography
                        component="span"
                        sx={{
                            fontStyle: 'italic',
                            fontSize: { xs: '0.9rem', md: '1rem' },
                            color: 'rgba(22, 163, 74, 0.9)',
                            textAlign: 'center',
                            px: 3,
                        }}
                    >
                        {placeholderLabel}
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

const featureRowSx = {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
    gap: { xs: 3, md: 4 },
    alignItems: 'center',
};

const copyCardSx = {
    p: { xs: 2.5, md: 3.5 },
    borderRadius: 3,
};

const ModulesSection = () => {
    return (
        <Box
            component="section"
            id="platform"
            aria-labelledby="platform-heading"
            sx={{
                py: { xs: 7, md: 8, lg: 8.5 },
                background: 'linear-gradient(180deg, #FAFBFF 0%, #FFFFFF 45%, #F5F8FF 100%)',
                borderTop: '1px solid rgba(0, 102, 255, 0.06)',
                borderBottom: '1px solid rgba(0, 102, 255, 0.06)',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '-20%',
                    right: '-10%',
                    width: 'min(480px, 55vw)',
                    height: 'min(480px, 55vw)',
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(0, 102, 255, 0.06) 0%, transparent 70%)',
                    pointerEvents: 'none',
                },
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: '-25%',
                    left: '-8%',
                    width: 'min(400px, 50vw)',
                    height: 'min(400px, 50vw)',
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(99, 102, 241, 0.05) 0%, transparent 70%)',
                    pointerEvents: 'none',
                },
            }}
        >
            <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                <Box sx={{ textAlign: 'center', maxWidth: 720, mx: 'auto', mb: { xs: 4, md: 5.5 } }}>
                    <Typography
                        component="span"
                        sx={{
                            display: 'inline-block',
                            fontSize: { xs: '0.6875rem', sm: '0.75rem' },
                            fontWeight: 700,
                            letterSpacing: '0.18em',
                            textTransform: 'uppercase',
                            color: '#0066FF',
                            bgcolor: 'rgba(0, 102, 255, 0.1)',
                            px: { xs: 1.75, sm: 2.25 },
                            py: 0.75,
                            borderRadius: 999,
                            mb: 2.5,
                        }}
                    >
                        The platform
                    </Typography>

                    <Typography
                        id="platform-heading"
                        variant="h2"
                        sx={{
                            fontSize: { xs: '1.75rem', sm: '2.125rem', md: '2.5rem' },
                            fontWeight: 800,
                            lineHeight: 1.15,
                            letterSpacing: '-0.035em',
                            color: '#0F172A',
                            mb: 2,
                        }}
                    >
                        Full visibility. Zero extra work.
                    </Typography>

                    <Typography
                        sx={{
                            fontSize: { xs: '1rem', sm: '1.0625rem', md: '1.125rem' },
                            lineHeight: 1.65,
                            color: 'rgba(15, 23, 42, 0.75)',
                            m: 0,
                        }}
                    >
                        Admins and therapists both work from meDZhub. Each sees what&apos;s relevant to
                        them - two views, one verified record.
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 5, md: 6 } }}>
                    {/* Operations + admin illustration */}
                    <Box sx={featureRowSx}>
                        <PlatformVisual
                            src={ADMIN_VISUAL}
                            alt="meDZhub admin and operations dashboard illustration"
                            badge="Operations"
                            placeholderLabel="Visual: Admin dashboard illustration"
                        />
                        <Box sx={copyCardSx}>
                            <Typography
                                component="h3"
                                sx={{
                                    fontSize: { xs: '1.1rem', md: '1.25rem' },
                                    fontWeight: 700,
                                    color: '#0F172A',
                                    mb: 1.5,
                                    letterSpacing: '-0.02em',
                                }}
                            >
                                For the centre owner / operations head
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: { xs: '0.9375rem', md: '1rem' },
                                    lineHeight: 1.7,
                                    color: 'rgba(15, 23, 42, 0.78)',
                                    m: 0,
                                    textAlign: 'left',
                                }}
                            >
                                You can see who&apos;s out, who&apos;s checked in, and what&apos;s pending,
                                without calling anyone. When the month closes, the numbers are already
                                there.
                            </Typography>
                        </Box>
                    </Box>

                    {/* Therapist illustration + copy (visual on right on desktop) */}
                    <Box
                        sx={{
                            ...featureRowSx,
                            '& > :first-of-type': { order: { xs: 2, md: 1 } },
                            '& > :last-of-type': { order: { xs: 1, md: 2 } },
                        }}
                    >
                        <Box sx={copyCardSx}>
                            <Typography
                                component="h3"
                                sx={{
                                    fontSize: { xs: '1.1rem', md: '1.25rem' },
                                    fontWeight: 700,
                                    color: '#0F172A',
                                    mb: 1.5,
                                    letterSpacing: '-0.02em',
                                }}
                            >
                                For the therapist
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: { xs: '0.9375rem', md: '1rem' },
                                    lineHeight: 1.7,
                                    color: 'rgba(15, 23, 42, 0.78)',
                                    m: 0,
                                    textAlign: 'left',
                                }}
                            >
                                Schedule, patient details, session notes - everything they need is in the
                                app. They check in, document on-site, and move on. Even without signal.
                            </Typography>
                        </Box>
                        <PlatformVisual
                            src={THERAPIST_VISUAL}
                            alt="meDZhub therapist mobile app illustration"
                            badge="THERAPIST APP"
                            placeholderLabel="Visual: Therapist app illustration"
                        />
                    </Box>
                </Box>

                {/* <Box sx={{ textAlign: 'center', mt: { xs: 4, md: 5 } }}>
                    <Button
                        variant="outlined"
                        component={Link}
                        to="/platform"
                        sx={{
                            textTransform: 'none',
                            borderRadius: 999,
                            color: '#FFFFFF',
                            px: 3.25,
                            py: 1.2,
                            fontSize: { xs: '0.95rem', md: '1rem' },
                            fontWeight: 600,
                            background: 'linear-gradient(135deg, #0066FF 0%, #4F46E5 100%)',
                            boxShadow: '0 10px 26px rgba(0, 102, 255, 0.26)',
                            border: 'none',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #0052CC 0%, #4338CA 100%)',
                                border: 'none',
                            },
                        }}
                    >
                        See how it works for your team
                    </Button>
                </Box> */}
            </Container>
        </Box>
    );
};

export default ModulesSection;
